import { ServiceDeliveryLocationBase } from "../json-schemas/base/service_delivery_location"
import { EmployeeIsCoveringCityResponse } from "../json-schemas/serializers/employee-is-covering-city-response"
import { BackofficeEmployeesListResponse } from "../json-schemas/serializers/backoffice-employees-list-response"
import { BackofficeEmployeesListStripeResponse } from "../json-schemas/serializers/backoffice-employees-list-stripe-response"
import { EmployeeTemporarySectorAutodetectProps } from "../json-schemas/serializers/employee-temporary-sector-autodetect-props"
import { httpDelete, httpGet, httpGetRoute, httpParamsRemoveDiacriticsToSortableNames, httpPatch, httpPatchMultipart, httpPost } from "../utils/http.service"
import { I18nService } from "../utils/i18n.service"
import { EmployeeInformationState } from "./employee-informations.service"
import { InterventionPlace } from "./offered-services.service"
import { ScheduleFrequency, ScheduleType } from "./schedules.service"
import { EmployeeBase } from "../json-schemas/base/employee"
import { SupplierBase } from "../json-schemas/base/supplier"
import { BackofficeEmployeesComputePeriodStatsResponse } from "../json-schemas/serializers/backoffice-employees-compute-period-stats-response"
import { BackofficeEmployeesListCastingAssignmentResponse } from "../json-schemas/serializers/backoffice-employees-list-casting-assignment-response"
import { AvailableAtResponse } from "../json-schemas/serializers/available-at-response"
import { UserPaymentModeEditResponse } from "../json-schemas/serializers/user-payment-mode-edit-response"
import { BackofficeEmployeeListEmployeePlanSubscriptionsIndexResponse } from "../json-schemas/serializers/backoffice-employee-list-employee-plan-subscriptions-index-response"
import { BackofficeEmployeeListEmployeePlanSubscriptionsCountPerScopeResponse } from "../json-schemas/serializers/backoffice-employee-list-employee-plan-subscriptions-count-per-scope-response"
import { BackofficeEmployeesListCesuContractFormResponse } from "../json-schemas/serializers/backoffice-employees-list-cesu-contract-form-response"
import { OfferedServiceBase } from "../json-schemas/base/offered_service"
import type { XlsxImportSubmitResponse } from "../utils/xlsx-import.service"
import { PhoneNumberBase } from "../json-schemas/base/phone_number"
import { EmployeeInformationBase } from "../json-schemas/base/employee_information"
import { isInseeFranceCountryCode } from "../../components/shared/insee-country-code-input.component"
import { BackofficeEmployeesMovesResponse } from "../json-schemas/serializers/backoffice-employees-moves-response"
import { BackofficeEmployeesListOnlyIdsResponse } from "../json-schemas/serializers/backoffice-employees-list-only-ids-response"
import { BackofficeEmployeesListIdAndNameResponse } from "../json-schemas/serializers/backoffice-employees-list-id-and-name-response"
import i18nScopedData from './employees.service.yml'

export type EmployeeSteps = 'principal_information' | 'profile_document' | 'intervention_sector' | 'planning' | 'skills' | 'third_party_apps' | 'urssaf_payment' | 'employee_services' | 'bank_details' | 'stripe_bank_details' | 'nova_export_config' | 'payment_facilities' | 'cesu_plus' | 'operator_segments'
export type EmployeeCoordinatesOrZones = EmployeeTemporarySectorAutodetectProps['employeeLocation'];

export interface RansackQuery {
  scope?: 'with_required_current_active_subscriptions' | 'exempted_of_subscription' | 'without_required_current_active_subscriptions_with_any_incoming_events' | 'without_required_current_active_subscriptions_without_incoming_events'
  'q[employee_information_offered_services_id_in]'?: number
  'q[employee_information_offered_services_service_id_in]'?: number
  'q[employee_information_state_in]'?: (EmployeeInformationState | 'deleted')[]
  'q[item_preferences_id_in]'?: number
  'q[service_delivery_location_id_eq]'?: ServiceDeliveryLocationBase['id']
  'q[sortable_name_or_phone_number_or_email_or_formatted_counter_cont]'?: string
  'q[with_intervention_places]'?: InterventionPlace
  'q[intervention_sector]'?: string
  'q[employee_information_broadcast_casting_eq]'?: boolean,
  'q[id_eq]'?: number
  'q[id_in][]'?: number[]
  'q[id_not_in][]'?: number[]
  'q[s]'?: 'sortable_name asc' | 'reversed_sortable_name asc' | 'event_count_last_30_days desc' | 'event_count_next_30_days desc' | 'stripe_account_requirements_current_deadline asc' | 'created_at asc' | 'created_at desc'
  'q[for_casting_id]'?: number
  'q[for_casting_same_service]'?: boolean
  'q[for_casting_broadcast]'?: boolean
  'q[employee_information_indexed_global_grade_lteq]'?: number
  'q[employee_information_indexed_global_grade_gteq]'?: number
  'q[bookmarks_customer_id_eq]'?: number
  'q[known_by_customer_id_eq]'?: number
  'q[with_required_current_active_employee_plan_subscriptions]'?: boolean
  'q[exempted_from_employee_plan_subscriptions]'?: boolean
  'q[without_required_current_active_employee_plan_subscriptions]'?: 'with_any_incoming_events' | 'without_any_incoming_events'
  'with_urssaf_payment'?: boolean
  with_operator_segments_restriction?: ('Employee')[]
  'available_for'?: {
    datetime: string
    duration: number
    schedule_type: ScheduleType
    weeks_interval?: number
    frequency?: ScheduleFrequency
    current_event_id?: number
    recurring_until?: string
  }
}


export type EmployeeCesuPlusStep = 'identity' | 'birth-date-and-location' | 'postal-address'
export const employeeCesuPlusOrderedSteps: EmployeeCesuPlusStep[] = ['identity', 'birth-date-and-location', 'postal-address']
export const employeeScopes : RansackQuery['scope'][] = ['with_required_current_active_subscriptions', 'exempted_of_subscription', 'without_required_current_active_subscriptions_with_any_incoming_events', 'without_required_current_active_subscriptions_without_incoming_events']

const i18nScoped = new I18nService(i18nScopedData)

export function employeeList (query?: RansackQuery): Promise<BackofficeEmployeesListResponse>
export function employeeList (query: RansackQuery, output: 'map'): Promise<BackofficeEmployeesListResponse>
export function employeeList (query: RansackQuery, output: 'stripe_index'): Promise<BackofficeEmployeesListStripeResponse>
export function employeeList (query: RansackQuery, output: 'casting_assignment'): Promise<BackofficeEmployeesListCastingAssignmentResponse>
export function employeeList (query: RansackQuery, output: 'employee_plan_subscriptions_index'): Promise<BackofficeEmployeeListEmployeePlanSubscriptionsIndexResponse>
export function employeeList (query: RansackQuery, output: 'employee_plan_subscriptions_count_per_scope'): Promise<BackofficeEmployeeListEmployeePlanSubscriptionsCountPerScopeResponse>
export function employeeList (query: RansackQuery, output: 'cesu_contract_form'): Promise<BackofficeEmployeesListCesuContractFormResponse>
export function employeeList (query: RansackQuery, output: 'only_ids'): Promise<BackofficeEmployeesListOnlyIdsResponse>
export function employeeList (query: RansackQuery, output: 'id_and_name'): Promise<BackofficeEmployeesListIdAndNameResponse>
export function employeeList (query: RansackQuery = {}, output?: 'map' | 'stripe_index' | 'casting_assignment' | 'employee_plan_subscriptions_index' | 'employee_plan_subscriptions_count_per_scope' | 'cesu_contract_form' | 'only_ids' | 'id_and_name'): Promise<BackofficeEmployeesListResponse | BackofficeEmployeesListStripeResponse | BackofficeEmployeesListCastingAssignmentResponse | BackofficeEmployeeListEmployeePlanSubscriptionsIndexResponse | BackofficeEmployeeListEmployeePlanSubscriptionsCountPerScopeResponse | BackofficeEmployeesListCesuContractFormResponse | BackofficeEmployeesListOnlyIdsResponse | BackofficeEmployeesListIdAndNameResponse> {
  // remove with_operator_segments_restriction filter if searched by name
  const withOperatorSegmentsRestriction = (query['q[sortable_name_or_phone_number_or_email_or_formatted_counter_cont]']?.length ?? 0) >= 3 ? undefined : query.with_operator_segments_restriction
  
  return httpGet(`/${httpGetRoute()}/employees`, httpParamsRemoveDiacriticsToSortableNames({ ...query, with_operator_segments_restriction: withOperatorSegmentsRestriction, output }))
}

export function employeesMoves (from_date: string, to_date: string, query: RansackQuery = {}): Promise<BackofficeEmployeesMovesResponse> {
  return httpGet(`/${httpGetRoute()}/employees/employee_moves`, httpParamsRemoveDiacriticsToSortableNames({ from_date, to_date, ...query }))
}

export type RankingQuery = {
  from: string,
  to: string,
  order_by: 'period_revenue_cents' | 'average_rating' | 'employee_since' | 'duration_for_deliveries',
  'q[id_not_in][]'?: number[]
}

export function employeeRanking (query: RankingQuery): Promise<BackofficeEmployeesComputePeriodStatsResponse> {
  return httpGet(`/${httpGetRoute()}/employees/ranking`, query)
}

export function employeeEditPaymentFacilities (id: EmployeeBase['id']): Promise<UserPaymentModeEditResponse> {
  if (window.currentUser.type === 'Employee') return httpGet(`/${httpGetRoute()}/payment_facilities`)
  return httpGet(`/${httpGetRoute()}/employees/${id}/payment_facilities`)
}

export function employeeCreate (body: any, otherParams?: any) {
  return httpPost(`/${httpGetRoute()}/employees`, { employee: body, ...otherParams })
}

export function employeeCreateBulk (bodies: any, skipedErrors: string[] | 'all'): Promise<XlsxImportSubmitResponse> {
  return httpPost(`/${httpGetRoute()}/employees/create_bulk`, { employees: bodies, skipped_errors: skipedErrors })
}

export function employeeUpdate (body: any, step: EmployeeSteps) {
  if (window.currentUser.type === 'Employee') var url = `/account_employee?step=${step}`
  else url = `/${httpGetRoute()}/employees/${body.id}?step=${step}`
  return httpPatch(url, { employee: body })
}

export function employeeUpdateMultipart (id: number, body: FormData, step:EmployeeSteps) {
  if (window.currentUser.type === 'Employee') var url = `/account_employee?step=${step}`
  else url = `/${httpGetRoute()}/employees/${id}?step=${step}`
  return httpPatchMultipart(url, body)
}

export function employeeIsAvailable (id: number, body: RansackQuery['available_for']): Promise<AvailableAtResponse> {
  const path = window.currentUser.type !== 'Employee'
    ? `employees/${id}/available_at`
    : window.currentUser.id === id
    ? 'available_at'
    : `other_employee_available_at?id=${id}`

  return httpPost(`/${httpGetRoute()}/${path}`, body)
}

export function getUnavailabilityMessage (availableAtResponse: AvailableAtResponse): string | null {
  if (availableAtResponse.available || !availableAtResponse.reason) return null

  return i18nScoped.t('unavailable_reasons.' + availableAtResponse.reason, { event_title: availableAtResponse.event?.title })
}

export function employeeAcceptNextSubscriptionPrice () {
  return httpPost(`/${httpGetRoute()}/accept_next_subscription_price`, {})
}

export function employeeRefuseNextSubscriptionPrice () {
  return httpPost(`/${httpGetRoute()}/refuse_next_subscription_price`, {})
}

export function employeeReviewApplication (id: number, body: any) {
  return httpPatch(`/${httpGetRoute()}/employees/${id}/review_application`, { employee: { base_field_validation: body } })
}

export function employeeIsCoveringCity (id: number, params: any): Promise<EmployeeIsCoveringCityResponse> {
  return httpGet(`/${httpGetRoute()}/employees/${id}/is_covering_city`, params)
}

export function employeeActivate (id: EmployeeBase['id']) {
  return httpPost(`/${httpGetRoute()}/employees/${id}/activate`, {})
}

export function employeeDeactivate (id: EmployeeBase['id']) {
  return httpPost(`/${httpGetRoute()}/employees/${id}/deactivate`, {})
}

export function employeeDelete (id: EmployeeBase['id']) {
  return httpDelete(`/${httpGetRoute()}/employees/${id}`)
}

export function employeeCheckCesuRegistration (id: EmployeeBase['id']): Promise<{ registered: boolean, cesu_number: string | null }> {
  return httpPost(`/${httpGetRoute()}/employees/${id}/check_cesu_registration`, {})
}

export function employeeI18nStep (step: EmployeeSteps) {
  return i18nScoped.t(`step.${step}`)
}

export type EmployeeCesuPlus = Pick<EmployeeBase, 'civility' | 'first_name' | 'last_name' | 'birth_name' | 'birthdate' | 'birth_city'> & {
  default_phone_number_attributes: Pick<PhoneNumberBase, 'id' | 'phone_number'>
  employee_information_attributes: Pick<EmployeeInformationBase, 'birth_department_insee' | 'birth_country_insee' | 'cesu_address' | 'secu_number'>
}

type EmployeeCesuPlusValidationKey = keyof Omit<EmployeeCesuPlus, 'employee_information_attributes'> | `employee_information_attributes.${keyof EmployeeCesuPlus['employee_information_attributes']}`

function employeeCesuPlusGetValue (employee: EmployeeCesuPlus, key: EmployeeCesuPlusValidationKey): any {
  if (key.startsWith('employee_information_attributes.')) return employee.employee_information_attributes?.[key.split('.')[1] as keyof EmployeeCesuPlus['employee_information_attributes']]
  return employee[key as keyof Omit<EmployeeCesuPlus, 'employee_information_attributes'>]
}

const employeeCesuPlusValidations: Record<EmployeeCesuPlusValidationKey, (val: any, employee: EmployeeCesuPlus) => string[]> = {
  civility: val => val ? [] : [i18nScoped.t('cesu_plus_validations.civility_required')],
  last_name: val => val?.trim() ? [] : [i18nScoped.t('cesu_plus_validations.last_name_required')],
  first_name: val => val?.trim() ? [] : [i18nScoped.t('cesu_plus_validations.first_name_required')],
  birth_name: val => val?.trim() ? [] : [i18nScoped.t('cesu_plus_validations.birth_name_required')],
  default_phone_number_attributes: val => val?.phone_number?.trim() ? [] : [i18nScoped.t('cesu_plus_validations.phone_number_required')],
  birthdate: val => val ? [] : [i18nScoped.t('cesu_plus_validations.birthdate_required')],
  birth_city: val => val?.trim() ? [] : [i18nScoped.t('cesu_plus_validations.birth_city_required')],
  'employee_information_attributes.birth_country_insee': val => val ? [] : [i18nScoped.t('cesu_plus_validations.birth_country_required')],
  'employee_information_attributes.birth_department_insee': (val, employee) => isInseeFranceCountryCode(employee.employee_information_attributes?.birth_country_insee) && !val ? [i18nScoped.t('cesu_plus_validations.birth_department_required')] : [],
  'employee_information_attributes.secu_number': val => val?.trim() ? [] : [i18nScoped.t('cesu_plus_validations.secu_number_required')],
  'employee_information_attributes.cesu_address': val => val ? [] : [i18nScoped.t('cesu_plus_validations.postal_address_required')]
}

export function employeeCesuPlusI18nValidate (employee: EmployeeCesuPlus, checkedProperties?: EmployeeCesuPlusValidationKey[]): { property: EmployeeCesuPlusValidationKey, message: string }[] {
  return (checkedProperties || Object.keys(employeeCesuPlusValidations) as EmployeeCesuPlusValidationKey[])
    .flatMap(property => employeeCesuPlusValidations[property](employeeCesuPlusGetValue(employee, property), employee).map(message => ({ property, message })))
}

export function employeeCesuPlusI18nValidateStep (step: EmployeeCesuPlusStep, employee: EmployeeCesuPlus) {
  const propertiesPerStep: { [step in EmployeeCesuPlusStep]: EmployeeCesuPlusValidationKey[] } = {
    'identity': ['civility', 'last_name', 'first_name', 'birth_name', 'default_phone_number_attributes', 'employee_information_attributes.secu_number'],
    'birth-date-and-location': ['birthdate', 'birth_city', 'employee_information_attributes.birth_country_insee', 'employee_information_attributes.birth_department_insee'],
    'postal-address': ['employee_information_attributes.cesu_address']
  }

  return employeeCesuPlusI18nValidate(employee, propertiesPerStep[step])
}

export function employeeCesuPlusI18nStep (step: EmployeeCesuPlusStep): string {
  return i18nScoped.t('cesu_plus_steps.' + step)
}

export function employeeCesuPlusGetNextStep (step: EmployeeCesuPlusStep): EmployeeCesuPlusStep | null {
  return employeeCesuPlusOrderedSteps[employeeCesuPlusOrderedSteps.indexOf(step) + 1] || null
}

export function employeeI18nScopes (scope: RansackQuery['scope']) {
  return i18nScoped.t(`scope.${scope}`)
}

export function employeeI18nConcatAge (employee: Partial<Pick<EmployeeBase, 'age'>> & { market: Pick<SupplierBase, 'display_employees_age?'> }) {
  if (employee.age && employee.market['display_employees_age?']) return `, ${i18nScoped.t('age', { count: employee.age })}`
  return null
}

export function employeeGetStepUrl (step: EmployeeSteps, id: number) {
  if (window.currentUser.type === 'Employee') return `/account_employee/${step || 'sumup'}`
  return `/${httpGetRoute()}/employees/${id}/${step || ''}`
}

export async function employeeIsDirectlyPaid (employeeId: EmployeeBase['id'], offeredServiceId: OfferedServiceBase['id']) {
  const baseUrl = window.currentUser.type === 'Employee' ? `/${httpGetRoute()}` : `/${httpGetRoute()}/employees/${employeeId}`
  const result: { is_directly_paid: boolean } = await httpGet(`${baseUrl}/is_employee_directly_paid`, { offered_service_id: offeredServiceId })
  return result.is_directly_paid
}
